import type { VorankUnterschriftArt } from './vorankUnterschriftTypes';

/**
 * Mail an den Ersteller der Fassung, wenn der Bauherr die Vorankündigung über
 * den Link unterschrieben oder eine Korrektur gemeldet hat
 * (docs/vorankuendigung-unterschrift-plan.md §4.3, Route
 * `api/vorank-unterschrift.ts`). Text, kein Anhang, nie an den Bauherrn.
 *
 * Reine Funktionen ohne Supabase und ohne Browser-APIs: die Datei läuft in der
 * Vercel-Funktion. Deshalb nur `import type` (fällt beim Übersetzen weg) und
 * KEIN Import aus `../mailLayout` oder `../material/materialMail` — Muster
 * `materialMail.ts`: das HTML steht hier schlicht und selbst maskiert.
 *
 * Link: Die App kennt keinen Deep-Link in ein Projekt (sie liest keine
 * Projekt-ID aus der Adresse). Der Link führt deshalb auf die App (Ursprung
 * der Anfrage, `appUrsprung` in materialMail.ts), die Mail nennt den Weg
 * „Projekt → Vorankündigung“.
 */

/** Ziel des Links, wenn der übergebene nicht wie eine Web-Adresse aussieht (PROD). */
export const VORANK_MAIL_APP_RUECKFALL = 'https://app.gefas-mbh.de';

export interface VorankMail {
    betreff: string;
    text: string;
    html: string;
}

interface VorankMailBasis {
    projektName: string | null;
    projektNummer: string | null;
    /** Name des Bauherrn: Unterzeichner bzw. Melder der Korrektur. */
    name: string;
    /** ISO-Zeitpunkt der Unterschrift bzw. Meldung. */
    am: string;
    /** Link in die App (Ursprung der Anfrage). */
    projektLink: string;
}

export interface VorankMailUnterschriftDaten extends VorankMailBasis {
    /** Freiwillige Funktion des Unterzeichners („Geschäftsführer“). */
    funktion?: string | null;
    /** `bild` = Unterschrift als Bild hochgeladen (E5). */
    art?: VorankUnterschriftArt | null;
    /** Eingetragenes Datum `YYYY-MM-DD` (E6) — genannt, wenn es vom Eingangstag abweicht. */
    datum?: string | null;
}

export interface VorankMailKorrekturDaten extends VorankMailBasis {
    /** Korrekturwunsch, mehrzeilig, ≤ 2000 Zeichen. */
    text: string;
}

/** Maskiert Nutzerdaten für HTML (Text und Attribute). */
export const escHtml = (wert: string | null | undefined): string =>
    String(wert ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

/** Steuer- und Formatzeichen raus, Leerraum zusammengezogen — für Betreff und Zeilen. */
const einzeilig = (wert: string | null | undefined): string =>
    String(wert ?? '').replace(/\p{Cc}/gu, ' ').replace(/\p{Cf}/gu, '').replace(/\s+/g, ' ').trim();

/** Mehrzeiliger Text: Umbrüche bleiben, sonstige Steuerzeichen fallen weg, höchstens eine Leerzeile. */
const mehrzeilig = (wert: string | null | undefined): string =>
    String(wert ?? '')
        .replace(/\r\n?/g, '\n')
        .replace(/[^\S\n]+/g, ' ')
        .replace(/(?!\n)\p{Cc}/gu, '')
        .replace(/\p{Cf}/gu, '')
        .split('\n')
        .map((z) => z.trim())
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();

const BERLIN = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
});

/**
 * ISO-Zeitpunkt → „28.10.2026, 14:03 Uhr“ in deutscher Ortszeit, egal in
 * welcher Zeitzone der Server läuft (Vercel: UTC). Ungültig oder leer → null.
 */
export const berlinZeitpunkt = (iso: string | null | undefined): string | null => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const teile: Record<string, string> = {};
    for (const t of BERLIN.formatToParts(d)) teile[t.type] = t.value;
    return `${teile.day}.${teile.month}.${teile.year}, ${teile.hour}:${teile.minute} Uhr`;
};

/** ISO-Zeitpunkt → Berliner Kalendertag „YYYY-MM-DD“; ungültig oder leer → null. */
const berlinIsoTagAus = (iso: string | null | undefined): string | null => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const teile: Record<string, string> = {};
    for (const t of BERLIN.formatToParts(d)) teile[t.type] = t.value;
    return `${teile.year}-${teile.month}-${teile.day}`;
};

/**
 * Das eingetragene Datum (E6) als „27.10.2026“ — nur, wenn es ein
 * Kalendertag `YYYY-MM-DD` ist UND vom Berliner Tag des Eingangs abweicht;
 * sonst null (dann sagt der Eingang schon alles).
 */
export const abweichendesDatum = (datum: string | null | undefined, am: string | null | undefined): string | null => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((datum || '').trim());
    if (!m) return null;
    const eingang = berlinIsoTagAus(am);
    return eingang && eingang !== m[0] ? `${m[3]}.${m[2]}.${m[1]}` : null;
};

/** „6254 Neubau Kita“, nur Nummer, nur Name — oder null, wenn beides fehlt. */
export const projektBezeichnung = (nummer: string | null | undefined, name: string | null | undefined): string | null => {
    const teile = [einzeilig(nummer), einzeilig(name)].filter(Boolean);
    return teile.length ? teile.join(' ') : null;
};

/** Nur http(s)-Adressen kommen als Link in die Mail; alles andere wird der PROD-Ursprung. */
const sichererLink = (link: string): string => {
    const l = (link || '').trim().replace(/\/+$/, '');
    return /^https?:\/\/[^\s"'<>]+$/i.test(l) ? l : VORANK_MAIL_APP_RUECKFALL;
};

const FUSSZEILE_TEXT = 'Diese E-Mail wurde automatisch über die SiGeKo Smart Report Cloud versandt.';

const RAHMEN = "font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;color:#334155;line-height:1.6;max-width:760px;";

interface Bausteine {
    betreff: string;
    /** Absätze als Klartext (unmaskiert). */
    absaetze: string[];
    /** Zitierter Korrekturtext, mehrzeilig (unmaskiert); null = keiner. */
    zitat: string | null;
    link: string;
}

const zusammensetzen = (b: Bausteine): VorankMail => {
    const wegZurApp = 'In der App: Projekt öffnen → Vorankündigung';
    const text = [
        b.betreff,
        '',
        ...b.absaetze.flatMap((a, i) => {
            const block = [a];
            // Das Zitat folgt dem ersten Absatz (Einleitung der Korrektur).
            if (i === 0 && b.zitat) block.push('', ...b.zitat.split('\n').map((z) => `> ${z}`));
            return [...block, ''];
        }),
        `${wegZurApp}: ${b.link}`,
        '',
        FUSSZEILE_TEXT,
    ].join('\n');

    const absaetzeHtml = b.absaetze
        .map((a, i) => {
            const p = `    <p style="margin:0 0 16px 0;">${escHtml(a)}</p>`;
            if (i !== 0 || !b.zitat) return p;
            const zitat = `    <div style="margin:0 0 16px 0;padding:10px 14px;border-left:3px solid #f59e0b;background:#fffbeb;color:#0f172a;white-space:pre-wrap;">${escHtml(b.zitat)}</div>`;
            return `${p}\n${zitat}`;
        })
        .join('\n');

    const html = `<div style="${RAHMEN}">
    <div style="padding-bottom:14px;border-bottom:3px solid #2563eb;margin-bottom:20px;">
        <h2 style="color:#1e3a8a;margin:0;font-size:20px;">${escHtml(b.betreff)}</h2>
        <div style="color:#64748b;font-size:13px;margin-top:4px;">Vorankündigung nach BaustellV</div>
    </div>
${absaetzeHtml}
    <p style="margin:0 0 20px 0;">In der App: <strong>Projekt öffnen &rarr; Vorankündigung</strong>: <a href="${escHtml(b.link)}" style="color:#1d4ed8;">${escHtml(b.link)}</a></p>
    <div style="font-size:11px;color:#94a3b8;border-top:1px solid #e2e8f0;padding-top:14px;">
        Diese E-Mail wurde automatisch über die <strong>SiGeKo Smart Report Cloud</strong> versandt.
    </div>
</div>`;

    return { betreff: b.betreff, text, html };
};

/**
 * „Vorankündigung unterschrieben: <Projekt>“ — wer, wann, auf welchem Weg,
 * und wo die unterschriebene Fassung landet (Plan §7: beim nächsten Öffnen
 * der Vorankündigung legt die App sie unter „Dokumente“ ab).
 */
export const vorankMailUnterschrift = (a: VorankMailUnterschriftDaten): VorankMail => {
    const projekt = projektBezeichnung(a.projektNummer, a.projektName);
    const name = einzeilig(a.name) || 'ohne Namen';
    const funktion = einzeilig(a.funktion);
    const wer = funktion ? `${name} (${funktion})` : name;
    const wann = berlinZeitpunkt(a.am);
    const betreff = `Vorankündigung unterschrieben${projekt ? `: ${projekt}` : ''}`;
    const absaetze = [
        `Die Vorankündigung${projekt ? ` zum Projekt ${projekt}` : ''} wurde von ${wer}${wann ? ` am ${wann}` : ''} digital unterschrieben.`,
    ];
    const eingetragen = abweichendesDatum(a.datum, a.am);
    if (eingetragen) absaetze.push(`Als Datum der Unterschrift wurde der ${eingetragen} eingetragen — so steht es unter „Ort, Datum“.`);
    if (a.art === 'bild') absaetze.push('Die Unterschrift wurde als Bild hochgeladen.');
    absaetze.push('Beim nächsten Öffnen der Vorankündigung legt die App die unterschriebene Fassung unter „Dokumente“ ab.');
    return zusammensetzen({ betreff, absaetze, zitat: null, link: sichererLink(a.projektLink) });
};

/**
 * „Vorankündigung: Korrektur gewünscht – <Projekt>“ — wer, wann, der Text als
 * Zitat. Der Link des Bauherrn wird erst mit einer neuen Fassung ungültig
 * (E4); bis dahin kann er weiter unterschreiben.
 */
export const vorankMailKorrektur = (a: VorankMailKorrekturDaten): VorankMail => {
    const projekt = projektBezeichnung(a.projektNummer, a.projektName);
    const name = einzeilig(a.name) || 'Der Bauherr';
    const wann = berlinZeitpunkt(a.am);
    const betreff = `Vorankündigung: Korrektur gewünscht${projekt ? ` – ${projekt}` : ''}`;
    const zitat = mehrzeilig(a.text) || '(ohne Text)';
    const absaetze = [
        `${name} hat${wann ? ` am ${wann}` : ''} zur Vorankündigung${projekt ? ` des Projekts ${projekt}` : ''} eine Korrektur gemeldet:`,
        'Bitte die Vorankündigung anpassen und neu exportieren. Der Bauherr erhält mit der neuen PDF einen neuen Link; der bisherige wird damit ungültig.',
    ];
    return zusammensetzen({ betreff, absaetze, zitat, link: sichererLink(a.projektLink) });
};
